<template>
  <div>
    <Breadcrumb
      :items="breadcrumb"
      with-brodos
    />
    <ProductDetail
      v-if="offer?.product"
      :offer="offer"
      :transformed-alternatives="transformedAlternatives"
    />
  </div>
</template>

<script lang="ts" setup>
import { watch, ref, computed } from 'vue';
import { H3Error, setResponseHeader, setResponseStatus } from 'h3';
import { truncate } from 'lodash-es';
import {
  defineNuxtComponent,
  useLazyAsyncData,
  useRuntimeConfig,
  useNuxtApp,
  onMounted,
  useHead,
  useRoute,
} from '#imports';
import fetchOfferBySlugs from '~/lib/goliath/offer-by-slugs';
import { getMainImageFromOffer } from '~/lib/goliath-media';

import { generateJsonLdData, transformOfferAlternatives } from '@i22/rocket';
import { getHeader } from '~/helpers/page-header-compositon';
import ProductDetail from '~/components/product-detail.vue';
import Breadcrumb from '~/components/breadcrumb.vue';
import { calculateLastModifiedOffer } from '~/lib/last-modified-offer';

import type { OfferQuery } from '~/lib/goliath/goliath.types';
import { TrackAsync } from '~/helpers/page-tracking';
import { isProductPageIndexed } from '~/lib/page-index-helper';
import { useDowntimesStore } from '~/stores/downtimes';

defineNuxtComponent({
  name: 'ProductDetails',
});

const nuxtApp = useNuxtApp();
const config = useRuntimeConfig();
const $route = useRoute();
const event = nuxtApp.ssrContext?.event;

const downtimesStore = useDowntimesStore();

if (downtimesStore.maintenanceDowntime) {
  if (process.server) {
    // Make this page be stored not so long in nginx.
    setResponseHeader(event, 'Cache-Control', 'public, max-age=30');
  }
  // send the user to the error page. The page will rdr to /maintenance. This is to preserve the Browser-History. Set 404 for cache.
  throw nuxtApp.nuxt2Context.error({
    statusCode: 404,
    message: 'Maintenance',
    redirectToMaintenance: true,
  });
}

const productSlug = $route.params.slug;

if (!productSlug) {
  throw nuxtApp.nuxt2Context.error({ statusCode: 404, message: 'No product slug' });
}

const {
  data: offer,
  error: errorData,
  status,
} = useLazyAsyncData<OfferQuery['offerBySlugs']>(productSlug, async () => {
  try {
    const goliathData = await fetchOfferBySlugs({ productSlug });
    if (!goliathData?.product) {
      const error = new H3Error('Product not found');
      error.statusCode = 404;
      error.data = { productSlug };
      if (process.server) {
        setResponseStatus(event, 404);
      }
      return Promise.reject(error);
    }

    if (!goliathData?.product?.family?.primaryCategory) {
      const error = new H3Error('No primary category');
      error.statusCode = 404;
      error.data = { productSlug };
      if (process.server) {
        setResponseStatus(event, 404);
      }
      return Promise.reject(error);
    }
    if (process.server) {
      setResponseHeader(
        event,
        'last-modified',
        calculateLastModifiedOffer(goliathData, new Date(nuxtApp.$config.public.buildDate))
      );
    }
    return goliathData;
  } catch (error) {
    if (error.response?.status === 404) {
      const e = new H3Error('Product not found');
      e.statusCode = 404;
      e.data = { productSlug };
      if (process.server) {
        setResponseStatus(event, 404);
      }
      return Promise.reject(e);
    }
    return Promise.reject(error);
  }
});

// handle errors
if (errorData.value) {
  // Show error page
  nuxtApp.nuxt2Context.error(errorData.value);
}

const script = computed(() => {
  if (!offer.value?.product) return {};
  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(generateJsonLdData(offer.value, config.public.baseUrl), null, ''),
  };
});

// The title is set in "useLazyAsyncData" for SSR and here for CSR. in nuxt 3 the part in `useLazyAsyncData` is no longer needed!
const seoProductName = computed(() => {
  if (!offer.value?.product) return undefined;
  const transformedAlternatives = transformOfferAlternatives(
    offer.value?.productAlternatives || []
  );
  const productHasAlternatives =
    (transformedAlternatives.filter((entry) =>
      entry.properties.some((property) => property.items.length > 1)
    )?.length || 0) > 0;

  return productHasAlternatives ? offer.value.product.name : offer.value.product.family?.name;
});

const seoDescription = computed(() => {
  if (!offer.value) return '';
  const uspsText = offer.value.usps
    ?.slice(0, 3)
    ?.map((usp) => `${usp} ✓`)
    ?.join(' ');
  const descr = ['Jetzt online bestellen:', seoProductName.value, '-', uspsText].join(' ');
  return truncate(descr, { length: 155, separator: ' ' });
});

getHeader({
  title: computed(() => `${seoProductName.value} kaufen`),
  description: seoDescription,
  isIndexed: computed(() => isProductPageIndexed(offer.value)),
  ogImage: computed(() => getMainImageFromOffer(offer.value)?.medium),
});

// json-ld
useHead(() => {
  if (!offer.value?.product) return {};
  return {
    script: {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(generateJsonLdData(offer.value, config.public.baseUrl), null, ''),
    },
  };
});

// When we are in csr, the "watch" for tracking is triggered before the page is mounted.
// An "immediate" would trigger the "old" content
const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
});

watch(
  () => [offer.value, status.value, isMounted.value],
  () => {
    if (status.value === 'error') {
      nuxtApp.nuxt2Context.error(errorData.value || new Error('Product not found'));
    }

    if (
      isMounted.value !== true ||
      offer.value === null ||
      !['idle', 'success'].includes(status.value)
    ) {
      return;
    }

    TrackAsync(
      {
        page_type: 'product.individual',
        items: [offer.value],
      },
      { delay: 100 }
    );
  },
  { immediate: true }
);

const transformedAlternatives = computed(() => {
  if (!offer.value?.productAlternatives) return [];
  return transformOfferAlternatives(offer.value.productAlternatives);
});

const getBrandRoute = (brandSlug) => {
  const primaryCategory = offer.value?.product?.family?.primaryCategory?.path;
  return `/${primaryCategory}/_${brandSlug.toLowerCase()}`;
};

const breadcrumb = computed(() => {
  const items = [];
  if (!offer.value?.product) {
    return items;
  }
  const { product } = offer.value;
  if (product.bundle) {
    items.push({ label: product.family.name, to: $route.path });
  } else if (product.brand) {
    items.push({
      label: product.brand.name,
      to: getBrandRoute(product.brand.slug),
    });
  }
  items.push({
    label: `${product.model || ''} ${product.variantName || ''}`.trim(),
    to: $route.path,
  });

  return items.filter((i) => i.label !== '' && i.to !== '');
});
</script>
